/* eslint-disable no-case-declarations */
import {
    UPDATE_PROJECT_TITLE,
    TOGGLE_NAVIGATION,
    HIDE_TOAST,
    SELECTING_SECTION,
    RECEIVED_CATEGORIES,
    SELECTING_CATEGORY,
    RECEIVED_PRODUCTS,
    SELECT_PRODUCT,
    SELECT_NAMED_PRODUCT,
    SELECTING_PRODUCT,
    RECEIVED_PRODUCT_DETAILS,
    CACHE_PRODUCT_DETAILS,
    EDIT_PRODUCT,
    REMOVE_PRODUCT,
    BUYING,
    BOUGHT,
    START,
    RESET,
    SET_SKIN_TONE,
    SET_ACTIVE_FILTER,
    SET_TORSO_TEXT,
    SET_TORSO_OVERLAY,
    SET_HEAD_FEATURES,
    SET_TORSO_IMAGE_UPLOAD,
    CLEAR_TORSO_IMAGE_UPLOAD,
    SET_DISPLAY_IMAGE_UPLOAD,
    SET_DISPLAY_IMAGE_UPLOAD_DRAFT,
    CLEAR_DISPLAY_IMAGE_UPLOAD,
    SET_AFTER_CURSOR,
    SET_CURRENT_FILTERS,
    SET_LOADING_TRUE,
    SET_LOADING_MORE_TRUE,
    SET_CONFIG,
    SET_CURRENT_BUILD_INDEX,
    REMOVE_BUILD,
    REMOVE_SELECTED_BUILD,
    ADD_BUILD,
    ERROR_BUYING,
    RESET_ERROR_BUYING,
    PROJECT_ADDED_TO_CART,
    RESET_ADDED_PROJECTS,
    SET_CUSTOMISE_OVERLAY_OPEN,
    SET_SEARCH_OPEN,
    CURRENCY_CHANGED,
    REFRESH_SELECTED_PRICES,
} from './actions';
import { LOAD } from 'redux-storage';
import { isKeychainProductId, isKeychainVariantId } from './constants';

import SelectedProduct from './entities/SelectedProduct';

const emptyDisplayImageUpload = () => ({
    dataUri: null,
    compositeDataUri: null,
    productId: null,
    position: { x: 0, y: 0, scale: 1 },
});

/**
 * Redux reducers
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @package Minifigs
 */

const buildTemplate = {
    projectKey: null,
    projectTitle: null,
    selected: {
        hatsAndHair: null,
        heads: null,
        bodies: null,
        legs: null,
        display: null,
        keychain: null,
        accessories: [],
    },
    skintone: null,
    torsoText: {
        backText: '',
        frontText: '',
    },
    torsoOverlays: null,
    headFeatures: {
        eyebrows: null,
        eyes: null,
        mouth: null,
        facialHair: {
            type: null, // 'beard' or 'moustache' handle
            style: null, // style option handle
            styleImageUrl: null, // image URL for overlay
        },
        glasses: null,
        facialFeatures: null,
        facialFeaturesHandle: null,
    },
    torsoImageUpload: {
        dataUri: null,
        compositeDataUri: null,
        position: { x: 0, y: 0, scale: 1 },
    },
    displayImageUpload: {
        dataUri: null,
        compositeDataUri: null,
        productId: null,
        position: { x: 0, y: 0, scale: 1 },
    },
    displayImageUploadDraft: {
        dataUri: null,
        compositeDataUri: null,
        productId: null,
        position: { x: 0, y: 0, scale: 1 },
    },
    // Flat array of related product nodes, each augmented with baseProductId for removal tracking
    relatedProducts: [],
};

export const initialState = {
    // the selected product for each section
    // if a product has been selected it will be a SelectedProduct object
    loaded: false,
    initialDataLoaded: false,
    currentBuildIndex: 0,
    builds: [{ ...buildTemplate }],
    navigation: {
        // the section that's currently been selected
        selected: null,
    },
    browser: {
        // the name of the currently viewed section (e.g. 'Hats and Hair')
        name: null,
        // the selected category (e.g. 'Brown hair')
        selectedCategory: null,
        // the list of categories for the section (exclusive with products)
        categories: [],
        // the selected product only if the product needs customising
        selectedProduct: null,
        // the list of products for the section (exclusive with categories)
        products: [],
        // Prefetched full product details keyed by product.id
        productDetailsCache: {},
    },
    breadcrumbs: {
        section: null,
        category: null,
        product: null,
    },
    loading: false,
    loadingMore: false,
    loadingSection: null, // which section is currently loading products
    buying: false,
    finished: false,
    showNavigation: false,
    showToast: false,
    errorOnBuy: false,
    errorMessage: null,
    sections: [],
    filters: [],
    addedProjects: [],
    isScrolled: false, // ??
    isCustomiseOverlayOpen: false,
    isSearchOpen: false,
    activeCurrency: null,
};

export default function root(state = initialState, action) {
    const buildIndex = action?.payload?.buildIndex;
    switch (action.type) {
        case '@@redux/INIT':
            return {
                ...initialState,
                ...state,
                loaded: false,
            };

        case LOAD:
            const loadedState = { ...state, ...action.payload };
            const migratedBuilds = (loadedState.builds || []).map((b) => {
                if (!b || !b.selected) return b;
                let migrated = { ...b };
                if (!migrated.projectKey) {
                    try {
                        const maybeWords =
                            (window.randomWords && window.randomWords({ exactly: 3, join: '_' })) || null;
                        const shortId = `${Date.now().toString(36).slice(-3)}${Math.random()
                            .toString(36)
                            .replace(/[^a-z0-9]/g, '')
                            .slice(0, 3)}`;
                        migrated.projectKey = (maybeWords ? maybeWords.toLowerCase() : 'fig_project') + '_' + shortId;
                    } catch (e) {
                        migrated.projectKey = `fig_project_${Date.now().toString(36).slice(-5)}`;
                    }
                }
                // Backfill displayImageUpload for builds persisted before this field existed
                if (!migrated.displayImageUpload) {
                    migrated.displayImageUpload = {
                        dataUri: null,
                        compositeDataUri: null,
                        productId: null,
                        position: { x: 0, y: 0, scale: 1 },
                    };
                } else if (!Object.prototype.hasOwnProperty.call(migrated.displayImageUpload, 'productId')) {
                    migrated.displayImageUpload = {
                        ...migrated.displayImageUpload,
                        productId: migrated.selected?.display?.product?.id || null,
                    };
                }
                if (!migrated.displayImageUploadDraft) {
                    migrated.displayImageUploadDraft = emptyDisplayImageUpload();
                }
                const sel = { ...b.selected };
                // If new array already exists and is an array, trust it
                if (Array.isArray(sel.accessories)) {
                    // Remove any legacy keys that might still exist
                    delete sel.accessory1;
                    delete sel.accessory2;
                    delete sel.accessory3;
                    delete sel.accessory4;
                    // Migrate relatedProducts object -> array if necessary
                    let related = b.relatedProducts;
                    if (Array.isArray(related)) {
                        // already new format
                    } else if (related && typeof related === 'object') {
                        const arr = [];
                        Object.entries(related).forEach(([baseId, nodes]) => {
                            if (Array.isArray(nodes)) {
                                nodes.forEach((n) => arr.push({ ...n, baseProductId: baseId }));
                            }
                        });
                        related = arr;
                    } else {
                        related = [];
                    }
                    return { ...migrated, selected: sel, relatedProducts: related };
                }
                const acc = [];
                ['accessory1', 'accessory2', 'accessory3', 'accessory4'].forEach((k) => {
                    if (sel[k]) acc.push(sel[k]);
                    delete sel[k];
                });
                // Previous single accessories:null is ignored; create array
                sel.accessories = acc;
                // Migrate relatedProducts if necessary (same as above)
                let related = b.relatedProducts;
                if (Array.isArray(related)) {
                    // ok
                } else if (related && typeof related === 'object') {
                    const arr = [];
                    Object.entries(related).forEach(([baseId, nodes]) => {
                        if (Array.isArray(nodes)) {
                            nodes.forEach((n) => arr.push({ ...n, baseProductId: baseId }));
                        }
                    });
                    related = arr;
                } else {
                    related = [];
                }

                return { ...migrated, selected: sel, relatedProducts: related };
            });
            return { ...loadedState, builds: migratedBuilds, loaded: true };

        case SET_CONFIG:
            return {
                ...state,
                ...action.payload,
            };

        case SET_CUSTOMISE_OVERLAY_OPEN:
            return {
                ...state,
                isCustomiseOverlayOpen: action.payload.isOpen,
            };
        case SET_SEARCH_OPEN:
            return {
                ...state,
                isSearchOpen: action.payload.isOpen,
            };

        case UPDATE_PROJECT_TITLE:
            const { title } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                const newTitle = title;
                const selected = { ...builds[buildIndex].selected };
                const setItemTitle = (item) => {
                    if (!item) return;
                    const pfjv = item.productFormJsonValues;
                    if (!pfjv) return;
                    if (!pfjv.properties) pfjv.properties = {};
                    pfjv.properties.projectTitle = newTitle;
                };

                Object.keys(selected).forEach((selectedKey) => {
                    if (selectedKey === 'accessories' && Array.isArray(selected.accessories)) {
                        selected.accessories = selected.accessories.map((acc) => {
                            if (acc) {
                                // Defensive clone so we don't mutate previous state objects
                                const cloned = {
                                    ...acc,
                                    productFormJsonValues: acc.productFormJsonValues
                                        ? { ...acc.productFormJsonValues }
                                        : acc.productFormJsonValues,
                                };
                                if (cloned.productFormJsonValues && cloned.productFormJsonValues.properties) {
                                    cloned.productFormJsonValues.properties = {
                                        ...cloned.productFormJsonValues.properties,
                                    };
                                }
                                setItemTitle(cloned);
                                return cloned;
                            }
                            return acc;
                        });
                    } else {
                        // Clone non-array item before mutation for Redux immutability guarantees
                        if (selected[selectedKey]) {
                            selected[selectedKey] = {
                                ...selected[selectedKey],
                                productFormJsonValues: selected[selectedKey].productFormJsonValues
                                    ? { ...selected[selectedKey].productFormJsonValues }
                                    : selected[selectedKey].productFormJsonValues,
                            };
                            if (
                                selected[selectedKey].productFormJsonValues &&
                                selected[selectedKey].productFormJsonValues.properties
                            ) {
                                selected[selectedKey].productFormJsonValues.properties = {
                                    ...selected[selectedKey].productFormJsonValues.properties,
                                };
                            }
                        }
                        setItemTitle(selected[selectedKey]);
                    }
                });
                builds[buildIndex].projectTitle = newTitle;
                builds[buildIndex].selected = selected;

                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                };
            }
            return state;
        case 'SET_PROJECT_KEY': {
            const { projectKey, force } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((b) => ({ ...b }));
                if (force || !builds[buildIndex].projectKey) builds[buildIndex].projectKey = projectKey;
                return { ...state, builds };
            }
            return state;
        }
        case TOGGLE_NAVIGATION:
            return {
                ...state,
                showNavigation: !state.showNavigation,
            };

        case HIDE_TOAST:
            return {
                ...state,
                showToast: false,
            };
        //
        // Select a section, receive categories
        //
        case SELECTING_SECTION:
            const selectedSection = action.payload;
            const isSkinTone = selectedSection === 'skinTone';
            return {
                ...state,
                navigation: {
                    selected: selectedSection,
                },
                browser: {
                    ...state.browser,
                    categories: [],
                    products: [],
                    selectedProduct: state.isCustomiseOverlayOpen
                        ? state.browser.selectedProduct
                        : state.browser.selectedProduct,
                },
                breadcrumbs: {
                    section: selectedSection,
                    category: null,
                    product: state.isCustomiseOverlayOpen ? state.browser.selectedProduct : null,
                },
                loading: isSkinTone ? false : true,
                loadingSection: isSkinTone ? null : selectedSection,
                isCustomiseOverlayOpen: false,
            };
        case RECEIVED_CATEGORIES:
            return {
                ...state,
                browser: {
                    ...state.browser,
                    selectedCategory: null,
                    selectedProduct: null,
                    categories: action.payload,
                },
                loading: false,
                showNavigation: false,
                initialDataLoaded: state.initialDataLoaded || true,
            };
        //
        // Select a category, receive products
        //
        case SELECTING_CATEGORY:
            const incomingCategory = action.payload;
            const isSkinToneCategory = incomingCategory && incomingCategory.id === 'skinTone';
            return {
                ...state,
                browser: {
                    ...state.browser,
                    selectedCategory: incomingCategory,
                    selectedProduct: state.isCustomiseOverlayOpen ? state.browser.selectedProduct : null,
                },
                breadcrumbs: {
                    ...state.breadcrumbs,
                    category: incomingCategory,
                    product: state.isCustomiseOverlayOpen ? state.browser.selectedProduct : null,
                },
                loading: isSkinToneCategory ? false : true,
            };
        case RECEIVED_PRODUCTS:
            return {
                ...state,
                browser: {
                    ...state.browser,
                    products: action.payload,
                },
                loading: false,
                loadingSection: null,
                loadingMore: false,
                initialDataLoaded: state.initialDataLoaded || true,
            };

        //
        // Selecting a product or customising it
        //
        case SELECTING_PRODUCT:
            // If we're re-selecting the same product while already on the customise screen (e.g. skin tone change),
            // don't force loading=true again – this was causing the app to get stuck in a perpetual loading state
            // because no new product details fetch was dispatched to flip it back to false.
            const incomingProduct = action.payload;
            const sameProductAlreadyLoaded =
                state.browser.selectedProduct &&
                state.browser.selectedProduct.id === incomingProduct?.id &&
                (state.browser.selectedProduct.product_attribute ||
                    state.browser.selectedProduct.variations ||
                    state.browser.selectedProduct.attribute_terms);

            const cachedDetails = state.browser.productDetailsCache?.[incomingProduct?.id];
            return {
                ...state,
                browser: {
                    ...state.browser,
                    selectedProduct: cachedDetails
                        ? {
                              ...incomingProduct,
                              ...cachedDetails.product,
                              variations: cachedDetails.variations,
                              product_attribute: cachedDetails.product_attribute,
                              attribute_terms: cachedDetails.attributeTerms,
                          }
                        : incomingProduct,
                },
                breadcrumbs: {
                    ...state.breadcrumbs,
                    product: incomingProduct,
                },
                loading: cachedDetails ? false : sameProductAlreadyLoaded ? state.loading : true,
                isCustomiseOverlayOpen: true,
            };
        case RECEIVED_PRODUCT_DETAILS:
            return {
                ...state,
                browser: {
                    ...state.browser,
                    selectedProduct: {
                        ...state.browser.selectedProduct,
                        ...(action.payload.product || {}),
                        variations: action.payload.variations,
                        product_attribute: action.payload.product_attribute,
                        attribute_terms: action.payload.attributeTerms,
                        productFormJsonValues: action.payload.productFormJsonValues,
                    },
                },
                loading: false,
                initialDataLoaded: state.initialDataLoaded || true,
            };
        case CACHE_PRODUCT_DETAILS:
            if (!action.payload?.product?.id) return state;
            return {
                ...state,
                browser: {
                    ...state.browser,
                    productDetailsCache: {
                        ...state.browser.productDetailsCache,
                        [action.payload.product.id]: {
                            product: action.payload.product,
                            variations: action.payload.variations,
                            product_attribute: action.payload.product_attribute,
                            attributeTerms: action.payload.attributeTerms,
                        },
                    },
                },
            };
        case SELECT_PRODUCT:
            const { product, attributes, addons, product_attribute, attributeTerms, productFormJsonValues } =
                action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                const currentBuild = builds[state.currentBuildIndex];
                const newSelectedProduct = new SelectedProduct(
                    product,
                    state.browser.selectedCategory,
                    attributes,
                    addons,
                    product_attribute,
                    attributeTerms,
                    productFormJsonValues
                ).toJSON();

                // Clear torso image upload if changing bodies product
                if (state.navigation.selected === 'bodies') {
                    const existingBodyProduct = currentBuild?.selected?.bodies?.product;
                    if (existingBodyProduct && existingBodyProduct.id !== product.id) {
                        currentBuild.torsoImageUpload = {
                            dataUri: null,
                            compositeDataUri: null,
                            position: { x: 0, y: 0, scale: 1 },
                        };
                    }
                }

                // Promote a draft display upload only once its product is added to the build.
                if (state.navigation.selected === 'display') {
                    const currentDisplayUpload = currentBuild?.displayImageUpload;
                    const currentDisplayUploadDraft = currentBuild?.displayImageUploadDraft;
                    const draftMatchesProduct =
                        currentDisplayUploadDraft?.productId === product.id &&
                        (currentDisplayUploadDraft.dataUri || currentDisplayUploadDraft.compositeDataUri);
                    const committedMatchesProduct = currentDisplayUpload?.productId === product.id;
                    currentBuild.displayImageUpload = draftMatchesProduct
                        ? currentDisplayUploadDraft
                        : committedMatchesProduct
                          ? currentDisplayUpload
                          : emptyDisplayImageUpload();
                    currentBuild.displayImageUploadDraft = emptyDisplayImageUpload();
                }

                // Capture related products (if any) from the underlying product GraphQL response
                const relatedNodes = product?.relatedProduct?.references?.nodes;
                const existingRelated = Array.isArray(currentBuild.relatedProducts)
                    ? [...currentBuild.relatedProducts]
                    : [];
                // Remove any previous entries for this base product id
                const filtered = existingRelated.filter((r) => r.baseProductId !== product.id);
                if (Array.isArray(relatedNodes) && relatedNodes.length > 0) {
                    const augmented = relatedNodes.map((n) => ({ ...n, baseProductId: product.id }));
                    currentBuild.relatedProducts = [...filtered, ...augmented];
                } else {
                    currentBuild.relatedProducts = filtered;
                }

                // Special handling for accessories array
                if (state.navigation.selected === 'accessories') {
                    const existing = Array.isArray(currentBuild.selected.accessories)
                        ? currentBuild.selected.accessories
                        : [];
                    // Prevent duplicate accessories: replace any existing entry with same product id
                    const filtered = existing.filter((acc) => acc?.product?.id !== newSelectedProduct?.product?.id);
                    builds[state.currentBuildIndex].selected = {
                        ...currentBuild.selected,
                        accessories: [...filtered, newSelectedProduct],
                    };
                } else {
                    // Global keychain detection independent of navigation.selected
                    let isKeychain = isKeychainProductId(product?.id);
                    if (!isKeychain) {
                        const variantIds = [];
                        if (newSelectedProduct?.product?.variants?.nodes) {
                            newSelectedProduct.product.variants.nodes.forEach((v) => v?.id && variantIds.push(v.id));
                        } else if (newSelectedProduct?.product?.variants?.edges) {
                            newSelectedProduct.product.variants.edges.forEach(
                                (e) => e?.node?.id && variantIds.push(e.node.id)
                            );
                        }
                        const normalise = (raw) => (raw ? raw.split('/').pop() : raw);
                        isKeychain =
                            variantIds.map(normalise).some(isKeychainVariantId) ||
                            isKeychainVariantId(normalise(newSelectedProduct?.attributes?.id)) ||
                            isKeychainVariantId(normalise(newSelectedProduct?.productMatchAttr?.id));
                    }
                    if (isKeychain) {
                        builds[state.currentBuildIndex].selected = {
                            ...currentBuild.selected,
                            keychain: newSelectedProduct,
                        };
                    } else {
                        builds[state.currentBuildIndex].selected = {
                            ...currentBuild.selected,
                            [state.navigation.selected]: newSelectedProduct,
                        };
                    }
                }

                // After selection mutation: prune relatedProducts whose baseProductId no longer present in any selected slot
                const presentIds = new Set();
                const sel = builds[state.currentBuildIndex].selected;
                ['hatsAndHair', 'heads', 'bodies', 'legs', 'display', 'keychain'].forEach((k) => {
                    const item = sel[k];
                    if (item?.product?.id) presentIds.add(item.product.id);
                });
                if (Array.isArray(sel.accessories)) {
                    sel.accessories.forEach((acc) => acc?.product?.id && presentIds.add(acc.product.id));
                }
                currentBuild.relatedProducts = (currentBuild.relatedProducts || []).filter((r) =>
                    presentIds.has(r.baseProductId)
                );

                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                    isCustomiseOverlayOpen: false,
                };
            }
            return state;
        case SELECT_NAMED_PRODUCT:
            const catName = action.payload.product_category.id ?? action.payload.product_category;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                const currentBuild = builds[state.currentBuildIndex];
                const newSelectedProduct = new SelectedProduct(
                    action.payload.product,
                    catName ? action.payload.product_category : state.browser.selectedCategory,
                    action.payload.attributes,
                    action.payload.addons,
                    action.payload.product_attribute,
                    action.payload.attributeTerms,
                    action.payload.productFormJsonValues
                ).toJSON();

                // Clear torso image upload if changing bodies product
                if (catName === 'bodies') {
                    const existingBodyProduct = currentBuild?.selected?.bodies?.product;
                    if (existingBodyProduct && existingBodyProduct.id !== action.payload.product.id) {
                        currentBuild.torsoImageUpload = {
                            dataUri: null,
                            compositeDataUri: null,
                            position: { x: 0, y: 0, scale: 1 },
                        };
                    }
                }

                // Promote a draft display upload only once its product is added to the build.
                if (catName === 'display') {
                    const currentDisplayUpload = currentBuild?.displayImageUpload;
                    const currentDisplayUploadDraft = currentBuild?.displayImageUploadDraft;
                    const productId = action.payload.product.id;
                    const draftMatchesProduct =
                        currentDisplayUploadDraft?.productId === productId &&
                        (currentDisplayUploadDraft.dataUri || currentDisplayUploadDraft.compositeDataUri);
                    const committedMatchesProduct = currentDisplayUpload?.productId === productId;
                    currentBuild.displayImageUpload = draftMatchesProduct
                        ? currentDisplayUploadDraft
                        : committedMatchesProduct
                          ? currentDisplayUpload
                          : emptyDisplayImageUpload();
                    currentBuild.displayImageUploadDraft = emptyDisplayImageUpload();
                }

                const relatedNodes = action.payload.product?.relatedProduct?.references?.nodes;
                const existingRelated = Array.isArray(currentBuild.relatedProducts)
                    ? [...currentBuild.relatedProducts]
                    : [];
                const filtered = existingRelated.filter((r) => r.baseProductId !== action.payload.product.id);
                if (Array.isArray(relatedNodes) && relatedNodes.length > 0) {
                    const augmented = relatedNodes.map((n) => ({ ...n, baseProductId: action.payload.product.id }));
                    currentBuild.relatedProducts = [...filtered, ...augmented];
                } else {
                    currentBuild.relatedProducts = filtered;
                }

                if (catName === 'accessories') {
                    const existing = Array.isArray(currentBuild.selected.accessories)
                        ? currentBuild.selected.accessories
                        : [];
                    const filtered = existing.filter((acc) => acc?.product?.id !== newSelectedProduct?.product?.id);
                    builds[state.currentBuildIndex].selected = {
                        ...currentBuild.selected,
                        accessories: [...filtered, newSelectedProduct],
                    };
                } else {
                    let isKeychain = isKeychainProductId(newSelectedProduct?.product?.id);
                    if (!isKeychain) {
                        const variantIds = [];
                        if (newSelectedProduct?.product?.variants?.nodes) {
                            newSelectedProduct.product.variants.nodes.forEach((v) => v?.id && variantIds.push(v.id));
                        } else if (newSelectedProduct?.product?.variants?.edges) {
                            newSelectedProduct.product.variants.edges.forEach(
                                (e) => e?.node?.id && variantIds.push(e.node.id)
                            );
                        }
                        const normalise = (raw) => (raw ? raw.split('/').pop() : raw);
                        isKeychain =
                            variantIds.map(normalise).some(isKeychainVariantId) ||
                            isKeychainVariantId(normalise(newSelectedProduct?.attributes?.id)) ||
                            isKeychainVariantId(normalise(newSelectedProduct?.productMatchAttr?.id));
                    }
                    if (isKeychain) {
                        builds[state.currentBuildIndex].selected = {
                            ...currentBuild.selected,
                            keychain: newSelectedProduct,
                        };
                    } else {
                        builds[state.currentBuildIndex].selected = {
                            ...currentBuild.selected,
                            [catName]: newSelectedProduct,
                        };
                    }
                }

                // Prune relatedProducts whose base product no longer selected anywhere
                const presentIds = new Set();
                const sel2 = builds[state.currentBuildIndex].selected;
                ['hatsAndHair', 'heads', 'bodies', 'legs', 'display', 'keychain'].forEach((k) => {
                    const item = sel2[k];
                    if (item?.product?.id) presentIds.add(item.product.id);
                });
                if (Array.isArray(sel2.accessories)) {
                    sel2.accessories.forEach((acc) => acc?.product?.id && presentIds.add(acc.product.id));
                }
                currentBuild.relatedProducts = (currentBuild.relatedProducts || []).filter((r) =>
                    presentIds.has(r.baseProductId)
                );

                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                    isCustomiseOverlayOpen: false,
                };
            }
            return state;
        //
        // Edit product
        //
        case EDIT_PRODUCT:
            const currentBuildCategory =
                state.builds[state.currentBuildIndex].selected[action.payload.sectionId].category;
            const currentBuildProduct =
                state.builds[state.currentBuildIndex].selected[action.payload.sectionId].product;
            // const buildIndex = state?.currentBuildIndex;
            if (
                state.currentBuildIndex !== undefined &&
                state.currentBuildIndex >= 0 &&
                state.currentBuildIndex < state.builds.length
            ) {
                return {
                    ...state,
                    browser: {
                        ...state.browser,
                        selectedCategory: currentBuildCategory,
                        selectedProduct: currentBuildProduct,
                    },
                    navigation: {
                        ...state.navigation,
                        selected: action.payload.sectionId,
                    },
                    breadcrumbs: {
                        section: action.payload.sectionId,
                        category: currentBuildCategory,
                        product: currentBuildProduct,
                    },
                };
            }
            return state;
        //
        // Remove product
        //
        case REMOVE_PRODUCT:
            const { sectionId } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                const currentBuild = builds[state.currentBuildIndex];
                let currentRelated = Array.isArray(currentBuild.relatedProducts)
                    ? [...currentBuild.relatedProducts]
                    : [];

                // Removal logic for accessories: sectionId can be 'accessories:<productId>' or 'accessories:index'
                if (sectionId && sectionId.startsWith('accessories')) {
                    const parts = sectionId.split(':');
                    const identifier = parts[1];
                    let newAccessories = Array.isArray(currentBuild.selected.accessories)
                        ? [...currentBuild.selected.accessories]
                        : [];
                    let removedItems = [];
                    if (identifier !== undefined) {
                        if (/^\d+$/.test(identifier)) {
                            // numeric index
                            const idx = parseInt(identifier, 10);
                            if (idx >= 0 && idx < newAccessories.length) {
                                const [removed] = newAccessories.splice(idx, 1);
                                if (removed?.product?.id) removedItems.push(removed.product.id);
                            }
                        } else {
                            // treat as product id
                            newAccessories = newAccessories.filter((acc) => {
                                const match = acc?.product?.id === identifier || acc?.product === identifier;
                                if (match && acc?.product?.id) removedItems.push(acc.product.id);
                                return !match;
                            });
                        }
                    }
                    builds[state.currentBuildIndex].selected = {
                        ...currentBuild.selected,
                        accessories: newAccessories,
                    };
                    // Remove related mapping(s)
                    if (removedItems.length) {
                        currentRelated = currentRelated.filter((r) => !removedItems.includes(r.baseProductId));
                    }
                } else {
                    // Capture removed product id before nulling
                    const removed = currentBuild.selected[sectionId];
                    const removedProductId = removed?.product?.id;
                    builds[state.currentBuildIndex].selected = {
                        ...currentBuild.selected,
                        [sectionId]: null,
                    };
                    if (removedProductId) {
                        currentRelated = currentRelated.filter((r) => r.baseProductId !== removedProductId);
                    }
                    if (sectionId === 'heads') {
                        builds[state.currentBuildIndex].headFeatures = {
                            eyebrows: null,
                            eyes: null,
                            mouth: null,
                            facialHair: {
                                type: null,
                                style: null,
                                styleImageUrl: null,
                            },
                            glasses: null,
                            facialFeatures: null,
                            facialFeaturesHandle: null,
                        };
                    }
                }
                currentBuild.relatedProducts = currentRelated;
                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                };
            }
            return state;
        //
        // Buy
        //
        case BUYING:
            return {
                ...state,
                loading: true,
                buying: true,
            };
        case ERROR_BUYING:
            const { errorMessage } = action.payload;
            return {
                ...state,
                loading: false,
                buying: false,
                finished: false,
                errorOnBuy: true,
                errorMessage,
            };
        case RESET_ERROR_BUYING:
            return {
                ...state,
                errorOnBuy: false,
                errorMessage: null,
                finished: false,
            };
        case BOUGHT:
            // todo show finish screen
            return {
                ...state,
                loading: false,
                buying: false,
                finished: true,
            };
        //
        // Start screen
        //
        case START:
            return {
                ...state,
                navigation: {
                    selected: null,
                },
                showNavigation: false,
            };
        //
        // Reset
        //
        case RESET:
            return {
                ...initialState,
                builds: [{ ...buildTemplate }],
                sections: state.sections,
                filters: state.filters,
                loaded: state.loaded,
                initialDataLoaded: state.initialDataLoaded,
            };
        case SET_SKIN_TONE:
            const { skintone } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                builds[state.currentBuildIndex].skintone = skintone;
                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                };
            }
            return state;
        case SET_ACTIVE_FILTER:
            return {
                ...state,
                activeFilter: action.payload,
            };
        case SET_TORSO_TEXT: {
            const { torsoText } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                builds[state.currentBuildIndex].torsoText = {
                    backText: torsoText.backText !== undefined ? torsoText.backText : state.torsoText.backText,
                    frontText: torsoText.frontText !== undefined ? torsoText.frontText : state.torsoText.frontText,
                };
                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                };
            }
            return state;
        }
        case SET_TORSO_OVERLAY: {
            const { torsoOverlays } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                builds[state.currentBuildIndex].torsoOverlays = torsoOverlays;

                // get correct overlay ref whenver skintone is updated etc.
                const bodyPfjv = builds[state.currentBuildIndex].selected?.bodies?.productFormJsonValues;
                if (bodyPfjv?.properties) {
                    bodyPfjv.properties = { ...bodyPfjv.properties };
                    if (torsoOverlays?.torsoOverlayImage !== undefined) {
                        bodyPfjv.properties._overlayImage = torsoOverlays.torsoOverlayImage;
                    }
                    if (torsoOverlays?.handImage !== undefined) {
                        bodyPfjv.properties._overlayHands = torsoOverlays.handImage;
                    }
                }

                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                };
            }
            return state;
        }
        case SET_HEAD_FEATURES: {
            const { headFeatures } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => {
                    return { ...build };
                });
                builds[state.currentBuildIndex].headFeatures = headFeatures;
                return {
                    ...state,
                    builds: builds.map((build) => {
                        return { ...build };
                    }),
                };
            }
            return state;
        }
        case SET_TORSO_IMAGE_UPLOAD: {
            const { imageData } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => ({ ...build }));
                builds[buildIndex].torsoImageUpload = imageData;
                return {
                    ...state,
                    builds: builds.map((build) => ({ ...build })),
                };
            }
            return state;
        }
        case CLEAR_TORSO_IMAGE_UPLOAD: {
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => ({ ...build }));
                builds[buildIndex].torsoImageUpload = {
                    dataUri: null,
                    position: { x: 0, y: 0, scale: 1 },
                };
                return {
                    ...state,
                    builds: builds.map((build) => ({ ...build })),
                };
            }
            return state;
        }
        case SET_DISPLAY_IMAGE_UPLOAD: {
            const { imageData } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => ({ ...build }));
                builds[buildIndex].displayImageUpload = imageData;
                return {
                    ...state,
                    builds: builds.map((build) => ({ ...build })),
                };
            }
            return state;
        }
        case SET_DISPLAY_IMAGE_UPLOAD_DRAFT: {
            const { imageData } = action.payload;
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => ({ ...build }));
                builds[buildIndex].displayImageUploadDraft = imageData;
                return {
                    ...state,
                    builds: builds.map((build) => ({ ...build })),
                };
            }
            return state;
        }
        case CLEAR_DISPLAY_IMAGE_UPLOAD: {
            if (buildIndex !== undefined && buildIndex >= 0 && buildIndex < state.builds.length) {
                const builds = state.builds.map((build) => ({ ...build }));
                builds[buildIndex].displayImageUpload = emptyDisplayImageUpload();
                builds[buildIndex].displayImageUploadDraft = emptyDisplayImageUpload();
                return {
                    ...state,
                    builds: builds.map((build) => ({ ...build })),
                };
            }
            return state;
        }
        case SET_AFTER_CURSOR:
            return {
                ...state,
                paginationInfo: {
                    afterCursor: action.payload.afterCursor,
                    hasNextPage: action.payload.hasNextPage,
                },
            };
        case SET_CURRENT_FILTERS: {
            return {
                ...state,
                currentFilters: action.payload,
            };
        }
        case SET_LOADING_TRUE: {
            return {
                ...state,
                loading: true,
            };
        }
        case SET_LOADING_MORE_TRUE: {
            return {
                ...state,
                loadingMore: true,
            };
        }
        case SET_CURRENT_BUILD_INDEX: {
            const { buildIndex } = action.payload;
            if (buildIndex < state.builds.length) {
                return {
                    ...state,
                    currentBuildIndex: buildIndex,
                };
            }
            return state;
        }
        case REMOVE_BUILD: {
            const { buildIndex } = action.payload;
            if (buildIndex < state.builds.length) {
                const newBuilds = [...state.builds];
                newBuilds.splice(buildIndex, 1);
                if (newBuilds.length === 0) {
                    newBuilds.push({ ...buildTemplate }); // We always have one build in place, even if it's empty
                }
                return {
                    ...state,
                    currentBuildIndex:
                        buildIndex === state.currentBuildIndex || state.currentBuildIndex >= newBuilds.length
                            ? 0
                            : state.currentBuildIndex, // If the build we were selecting is the one we're deleting we set the current to 0 which we know exists
                    builds: [...newBuilds],
                    navigation: {
                        selected: null,
                    },
                };
            }
            return state;
        }
        case REMOVE_SELECTED_BUILD: {
            const { selectedBuild } = action.payload;
            const newBuilds = state.builds
                .filter((build) => build.projectTitle !== selectedBuild.projectTitle)
                .map((build) => {
                    return { ...build };
                });
            if (newBuilds.length === 0) {
                newBuilds.push({ ...buildTemplate }); // We always have one build in place, even if it's empty
            }
            return {
                ...state,
                currentBuildIndex:
                    buildIndex === state.currentBuildIndex || state.currentBuildIndex >= newBuilds.length
                        ? 0
                        : state.currentBuildIndex, // If the build we were selecting is the one we're deleting we set the current to 0 which we know exists
                builds: [...newBuilds],
                navigation: {
                    selected: null,
                },
            };
        }
        case PROJECT_ADDED_TO_CART: {
            const newAddedProjects = [...state.addedProjects, action.payload.projectTitle];
            return {
                ...state,
                addedProjects: newAddedProjects,
            };
        }
        case RESET_ADDED_PROJECTS: {
            return {
                ...state,
                addedProjects: [],
            };
        }
        case ADD_BUILD: {
            const initialData = action.payload ?? {};
            let newBuilds = [...state.builds];
            if (state.builds.length === 1 && !state.builds[0].projectTitle) {
                newBuilds = [];
            }

            newBuilds.push({ ...buildTemplate, ...initialData });
            return {
                ...state,
                currentBuildIndex: newBuilds.length - 1,
                builds: [...newBuilds],
                navigation: {
                    selected: null,
                },
            };
        }
        case 'SET_SCROLL_STATE': {
            return {
                ...state,
                isScrolled: action.payload,
            };
        }
        case CURRENCY_CHANGED: {
            return {
                ...state,
                activeCurrency: action.payload,
            };
        }
        case REFRESH_SELECTED_PRICES: {
            const priceMap = action.payload; // { variantGID: { amount, currencyCode } }
            const updatedBuilds = state.builds.map((build) => {
                const selected = build.selected;
                if (!selected) return build;

                const patchPrice = (item) => {
                    if (!item) return item;
                    let changed = false;
                    let newItem = { ...item };
                    if (item.attributes?.id && priceMap[item.attributes.id]) {
                        newItem.attributes = { ...item.attributes, price: priceMap[item.attributes.id] };
                        changed = true;
                    }
                    if (item.productMatchAttr?.id && priceMap[item.productMatchAttr.id]) {
                        newItem.productMatchAttr = {
                            ...item.productMatchAttr,
                            price: priceMap[item.productMatchAttr.id],
                        };
                        changed = true;
                    }
                    return changed ? newItem : item;
                };

                const newSelected = {};
                let anyChanged = false;
                Object.keys(selected).forEach((section) => {
                    if (section === 'accessories' && Array.isArray(selected[section])) {
                        const newAccs = selected[section].map(patchPrice);
                        newSelected[section] = newAccs;
                        if (newAccs.some((a, i) => a !== selected[section][i])) anyChanged = true;
                    } else {
                        const patched = patchPrice(selected[section]);
                        newSelected[section] = patched;
                        if (patched !== selected[section]) anyChanged = true;
                    }
                });

                if (!anyChanged) return build;
                return { ...build, selected: newSelected };
            });

            return {
                ...state,
                builds: updatedBuilds,
            };
        }
    }

    return state;
}
